import { createContext, useContext, type ReactNode } from 'react'
import type { DealFieldDef, RawDealFile } from '@/services/dealFile'
import { isWritableField } from './editableFields'
import type { DerivationOutcome } from './deriveCatalogue'
import type { FieldSourceRef } from './workspace/types'

export type DossierEdit = {
  unlocked: boolean
  draft: Record<string, unknown>
  raw: RawDealFile | undefined
  defs: DealFieldDef[]
  setField: (name: string, value: unknown) => void
  derivation: DerivationOutcome | null
  effectiveValue: (name: string) => unknown
  isDerivedField: (name: string) => boolean
  /** The formula of a value the engine derives and stores (lineage state
   *  Derived), or null for an input. A derived output is never an input box. */
  derivedFormula?: (name: string) => string | null
  /** The saved value's agreed state (Derived, Suggested, Confirmed, Stated)
   *  from the field's lineage, for the chip a slot shows beside it (C05).
   *  Null for a field the deal holds no figure for, or one a person is
   *  typing over and has not saved. */
  valueSource?: (name: string) => FieldSourceRef | null
  /** The fields a derived value is computed from: the inputs its lineage
   *  recorded, else the catalogue's derivation rules for that target. An
   *  unlocked dossier offers these, in place, wherever it shows the output,
   *  because the output itself is never typed over (R1). Optional so a surface
   *  that builds its own context (tests, the grid inspector) keeps working;
   *  absent, a derived value names its formula and offers no inputs. */
  inputsOf?: (name: string) => string[]
}

const DossierEditContext = createContext<DossierEdit | null>(null)

export function DossierEditProvider({
  value,
  children,
}: {
  value: DossierEdit
  children: ReactNode
}) {
  return <DossierEditContext.Provider value={value}>{children}</DossierEditContext.Provider>
}

const EMPTY_EDIT: DossierEdit = {
  unlocked: false,
  draft: {},
  raw: undefined,
  defs: [],
  setField: () => undefined,
  derivation: null,
  effectiveValue: () => undefined,
  isDerivedField: () => false,
}

export function useDossierEdit(): DossierEdit {
  const ctx = useContext(DossierEditContext)
  return ctx ?? EMPTY_EDIT
}

/** Null when rendered outside the dossier (grid inspector keeps its own edit). */
export function useOptionalDossierEdit(): DossierEdit | null {
  return useContext(DossierEditContext)
}

export function useFieldWritable(name: string): boolean {
  const { defs } = useDossierEdit()
  return isWritableField(name, defs)
}
